import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FunctionComponent,
  type KeyboardEvent,
} from "react";
import { FilterOutlined } from "@ant-design/icons";
import { Button, Dropdown, Empty, Input, theme, type InputRef, type MenuProps } from "antd";

import { type ChatTeamMemberDirectoryEntry } from "~/modules/Chat/hooks/useChatTeamMemberDirectory";

export const OWN_CHATS_MENU_KEY = "own_chats";

export interface ChatTrainerFilterDropdownProps {
  trainers: ChatTeamMemberDirectoryEntry[];
  userId: string;
  memberId?: string | null;
  selectedFilterKey: string;
  onSelectionChange: (key: string) => void;
}

const ChatTrainerFilterDropdown: FunctionComponent<ChatTrainerFilterDropdownProps> = ({
  trainers,
  userId,
  memberId,
  selectedFilterKey,
  onSelectionChange,
}) => {
  const { token } = theme.useToken();
  const [open, setOpen] = useState(false);
  const [popupQuery, setPopupQuery] = useState("");
  const [keyboardIndex, setKeyboardIndex] = useState<number | null>(null);
  const searchInputRef = useRef<InputRef>(null);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const menuItems = useMemo<NonNullable<MenuProps["items"]>>(
    () => [
      { key: OWN_CHATS_MENU_KEY, label: "Twoje czaty" },
      ...trainers
        .filter((trainer) => trainer.userId !== userId && trainer.id !== memberId)
        .map((trainer) => ({
          key: trainer.id,
          label: `${trainer.firstName} ${trainer.lastName}`,
        })),
    ],
    [trainers, userId, memberId],
  );

  const filteredItems = useMemo(() => {
    const q = popupQuery.trim().toLowerCase();
    if (!q) return menuItems;
    return menuItems.filter((item) => {
      if (!item || !("label" in item)) return false;
      const text = typeof item.label === "string" ? item.label : "";
      return text.toLowerCase().includes(q);
    });
  }, [menuItems, popupQuery]);

  const rows = useMemo(
    () =>
      filteredItems.flatMap((item) => {
        if (!item || typeof item.key !== "string" || !("label" in item)) return [];
        const labelText = typeof item.label === "string" ? item.label : item.key;
        return [{ key: item.key, label: labelText }];
      }),
    [filteredItems],
  );

  const applySelection = useCallback(
    (key: string) => {
      onSelectionChange(key);
      setOpen(false);
      setPopupQuery("");
      setKeyboardIndex(null);
    },
    [onSelectionChange],
  );

  useEffect(() => {
    setKeyboardIndex(null);
  }, [popupQuery]);

  useEffect(() => {
    setKeyboardIndex((i) => {
      if (i === null) return null;
      if (rows.length === 0) return null;
      return Math.min(i, rows.length - 1);
    });
  }, [rows.length]);

  useLayoutEffect(() => {
    if (keyboardIndex === null || !open) return;
    const el = optionRefs.current[keyboardIndex];
    if (!el) return;
    el.focus({ preventScroll: false });
    el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [keyboardIndex, open, rows]);

  const onSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      event.stopPropagation();
      if (rows.length === 0) {
        if (event.key === "Escape") {
          event.preventDefault();
          setOpen(false);
          setPopupQuery("");
          setKeyboardIndex(null);
        }
        return;
      }

      switch (event.key) {
        case "ArrowDown": {
          event.preventDefault();
          setKeyboardIndex(0);
          break;
        }
        case "Tab": {
          if (!event.shiftKey) {
            event.preventDefault();
            setKeyboardIndex(0);
          }
          break;
        }
        case "Escape": {
          event.preventDefault();
          setOpen(false);
          setPopupQuery("");
          setKeyboardIndex(null);
          break;
        }
        default:
          break;
      }
    },
    [rows],
  );

  const onOptionKeyDown = useCallback(
    (optionIndex: number, rowKey: string) => (event: KeyboardEvent<HTMLDivElement>) => {
      event.stopPropagation();
      const last = rows.length - 1;

      switch (event.key) {
        case "ArrowDown": {
          event.preventDefault();
          if (optionIndex < last) {
            setKeyboardIndex(optionIndex + 1);
          }
          break;
        }
        case "ArrowUp": {
          event.preventDefault();
          if (optionIndex === 0) {
            setKeyboardIndex(null);
            queueMicrotask(() => searchInputRef.current?.focus?.());
          } else {
            setKeyboardIndex(optionIndex - 1);
          }
          break;
        }
        case "Enter":
        case " ": {
          event.preventDefault();
          applySelection(rowKey);
          break;
        }
        case "Escape": {
          event.preventDefault();
          setOpen(false);
          setPopupQuery("");
          setKeyboardIndex(null);
          break;
        }
        case "Tab": {
          if (event.shiftKey) {
            if (optionIndex > 0) {
              event.preventDefault();
              setKeyboardIndex(optionIndex - 1);
            } else {
              event.preventDefault();
              setKeyboardIndex(null);
              queueMicrotask(() => searchInputRef.current?.focus?.());
            }
          } else if (optionIndex < last) {
            event.preventDefault();
            setKeyboardIndex(optionIndex + 1);
          }
          break;
        }
        default:
          break;
      }
    },
    [applySelection, rows],
  );

  const isFilterActive = selectedFilterKey !== OWN_CHATS_MENU_KEY;

  return (
    <Dropdown
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setPopupQuery("");
          setKeyboardIndex(null);
        }
      }}
      placement="bottomRight"
      overlayStyle={{
        overflow: "visible",
        padding: 0,
        maxHeight: "none",
        background: "transparent",
      }}
      trigger={["click"]}
      destroyOnHidden
      dropdownRender={() => (
        <div
          role="presentation"
          className="flex max-h-[min(480px,55vh)] min-h-0 min-w-[220px] max-w-[min(420px,calc(100vw-48px))] flex-col overflow-hidden rounded-md border border-solid border-neutral-200 bg-white py-2 shadow-lg"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="shrink-0 px-3 pb-2 pt-1">
            <Input
              ref={searchInputRef}
              allowClear
              placeholder="Szukaj trenera…"
              size="small"
              value={popupQuery}
              onChange={(event) => setPopupQuery(event.target.value)}
              onKeyDown={onSearchKeyDown}
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-2 pb-1">
            {rows.length === 0 ? (
              <Empty className="my-6" description="Brak wyników" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <div role="listbox" className="flex flex-col gap-0.5 py-0.5">
                {rows.map((row, index) => {
                  const selected = selectedFilterKey === row.key;
                  const keyboardFocus = keyboardIndex === index;
                  return (
                    <div
                      key={row.key}
                      id={`trainer-filter-option-${index}`}
                      ref={(el) => {
                        optionRefs.current[index] = el;
                      }}
                      role="option"
                      aria-selected={selected}
                      tabIndex={-1}
                      className="cursor-pointer rounded-md px-3 py-1.5 text-sm outline-none hover:bg-neutral-50"
                      style={{
                        ...(selected
                          ? {
                              backgroundColor: token.controlItemBgActive,
                              color: token.colorPrimary,
                            }
                          : {}),
                        ...(keyboardFocus ? { boxShadow: `inset 0 0 0 2px ${token.colorPrimary}` } : {}),
                      }}
                      onClick={() => applySelection(row.key)}
                      onKeyDown={onOptionKeyDown(index, row.key)}
                    >
                      {row.label}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    >
      <Button
        aria-label="Filtruj po trenerze"
        icon={<FilterOutlined />}
        type={isFilterActive ? "primary" : "default"}
      />
    </Dropdown>
  );
};

export default ChatTrainerFilterDropdown;
